import { Component, Input, OnInit, ViewChild, ChangeDetectorRef, OnDestroy, SimpleChanges, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ApexAxisChartSeries, ApexChart, ApexDataLabels, ApexGrid, ChartComponent, ApexPlotOptions, ApexLegend, ApexTooltip, ApexNonAxisChartSeries, ApexResponsive} from "ng-apexcharts";
import { NgApexchartsModule } from "ng-apexcharts";
import { Company } from "../../../../interfaces/company.interface";
import { getAssistantPromptByTag } from 'src/app/utils/assistant-prompt.util';
import {selectIsAsideOpen} from "../../../../state/ui/ui.selectors";
import {Store} from "@ngrx/store";
import {Subscription} from "rxjs";
import {PopupService} from "../../../../services/popup.service";

interface AreasOfExpertise {
  series: number[];
  labels: string[];
}

@Component({
  selector: 'app-area-of-expertise',
  standalone: true,
  imports: [CommonModule, NgApexchartsModule],
  styles: [`
    .animated-random-gradient-text {
      background: linear-gradient(90deg,
        var(--gradient-start),
        var(--gradient-mid),
        var(--gradient-end)
      );
      background-size: 400% auto;
      color: transparent;
      -webkit-background-clip: text;
      background-clip: text;
      animation: randomGradientShift 5s ease infinite;
    }
    .animated-dots::after {
      content: '...';
      display: inline-block;
      animation: loadingDots 1.5s infinite linear;
      overflow: hidden;
      color: transparent;
      vertical-align: bottom;
    }
    @keyframes randomGradientShift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes loadingDots {
      0%, 20% { content: '.'; }
      33%, 53% { content: '..'; }
      66%, 86% { content: '...'; }
    }
  `],
  template: `
      <div id="area-of-expertise">
        <ng-container *ngIf="hasData; else noData">
          <div class="mb-3"></div>
          <apx-chart
            #chart
            [series]="chartOptions.series"
            [chart]="chartOptions.chart"
            [labels]="chartOptions.labels"
            [colors]="chartOptions.colors"
            [legend]="chartOptions.legend"
            [plotOptions]="chartOptions.plotOptions"
            [tooltip]="chartOptions.tooltip"
            [grid]="chartOptions.grid"
          ></apx-chart>
            <button
              *ngIf="allExpertiseAreas?.length"
              (click)="showAllAreas()"
              class="absolute bottom-3 right-3 text-sm text-blue-500 hover:opacity-90 mt-2 text-center">
              Show All
            </button>
        </ng-container>
        <ng-template #noData>
          <div class="flex items-center justify-start text-gray-500 px-6">
            <!-- {{ loadingState ? 'Loading...' : 'N/A' }} -->

            <!-- Loading -->
            <div *ngIf="loadingState" class="flex items-center space-x-3">

              <div class="flex items-center space-x-3">
              <span [ngClass]="['relative', 'flex', 'items-center', size]">
                <span [ngClass]="['animate-ping', 'absolute', 'inline-flex', 'rounded-full', size, 'bg-' + color, 'opacity-75']"></span>
                <span [ngClass]="['relative', 'inline-flex', 'rounded-full', size, 'bg-' + color]"></span>
              </span>
                <p class="relative text-sm !text-secondary dark:text-white loading-text">
                <span class="animated-random-gradient-text">
                  Gathering data<span class="animated-dots"></span>
                </span>
                </p>
              </div>

            </div>

            <span *ngIf="!loadingState">N/A</span>

          </div>
        </ng-template>
      </div>
  `,
})
export class AreaOfExpertiseComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild("chart") chart!: ChartComponent;
  @Input() edit: boolean | undefined;
  @Input() reportSearch: any;
  @Input() company: Company;

  public chartOptions: any = null;
  areas_of_expertise: { series: any[]; labels: string[] } = { series: [], labels: [] };

  private asideSubscription: Subscription;

  allExpertiseAreas: { name: string; years: number }[] = [];

  hasData: boolean = false;
  loadingState: boolean = false;

  @Input() size: string = 'size-2';
  @Input() color: string = 'blue-sky';

  constructor(private store: Store, private popupService: PopupService, private cdr: ChangeDetectorRef) {
    this.initChartOptions();
  }

  ngOnInit(): void {

    this.asideSubscription = this.store.select(selectIsAsideOpen).subscribe(() => {
      setTimeout(() => {
        // console.log('[area-of-expertise.component.ts] selectIsAsideOpen')
        // this.updateChartLayout();
      }, 100);
    });

  }

  ngOnDestroy() {
    if (this.asideSubscription) {
      this.asideSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['company']?.currentValue) {
      const previousValue = changes['company'].previousValue;
      const currentValue = changes['company'].currentValue;

      // Check both insights and assistant_prompts for the data
      const previousData = this.getPromptData(previousValue);
      const currentData = this.getPromptData(currentValue);

      // Compare the actual data, not just the reference
      if (JSON.stringify(previousData) !== JSON.stringify(currentData)) {
        if (currentValue) {
          // Check if data exists in either insights or assistant_prompts
          const hasInsight = currentValue.insights?.expertise_areas?.response;
          const hasPrompt = currentValue.assistant_prompts?.some(
            (item: any) => item.tag === 'ASK_ASSISTANT_EXPERTISE_AREAS'
          );

          if (!hasInsight && !hasPrompt) {
            this.hasData = false;
            this.loadingState = true;
          } else {
            this.loadingState = false;
            this.initApexCharts(currentValue);
          }
        }
      }
    }
  }

  private getPromptData(company: any): any {
    if (!company) return null;
    // Try insights first (from WebSocket), then fall back to assistant_prompts
    return company.insights?.expertise_areas?.response ||
           company.assistant_prompts?.find((item: any) => item.tag === 'ASK_ASSISTANT_EXPERTISE_AREAS')?.content;
  }

  private initApexCharts(company: any) {
    const promptContent = getAssistantPromptByTag(company, 'ASK_ASSISTANT_EXPERTISE_AREAS');
    if (promptContent) {
      this.getAreasOfExpertise(promptContent);
    }
  }

  private getAreasOfExpertise(areasOfExpertise: any): void {
    if (!areasOfExpertise) {
      this.hasData = false;
      return;
    }

    try {
      if (!areasOfExpertise) {
        throw new Error("No data provided");
      }

      // Parse if string
      let parsedData = areasOfExpertise;
      if (typeof areasOfExpertise === 'string' && this.isValidJSON(areasOfExpertise)) {
        parsedData = JSON.parse(areasOfExpertise);
      }

      // Store all areas
      if (parsedData.all) {
        this.allExpertiseAreas = parsedData.all;
      }

      // Handle the chart data
      if (parsedData.top_5) {
        this.areas_of_expertise = {
          series: parsedData.top_5.series,
          labels: parsedData.top_5.labels
        };
        this.hasData = true;
      }
      // Fallback to old format if available
      else if (parsedData.series && parsedData.labels) {
        this.areas_of_expertise = parsedData;
      }
      else {
        this.hasData = false;
        throw new Error("Invalid data format");
      }

      this.updateChartData();
    } catch (error) {
      console.error("[getAreasOfExpertise] Failed to process data", error);
      this.areas_of_expertise = { series: [], labels: [] };
      this.allExpertiseAreas = [];
    }
  }

  showAllAreas(): void {
    if (!this.allExpertiseAreas?.length) return;

    const formattedContent = `
    <div>
      <div class="divide-y divide-gray-100">
        ${this.allExpertiseAreas.map(area => `
          <div class="flex justify-between items-center py-2">
            <div class="flex-1">
              <span class="text-secondary">${area.name}</span>
            </div>
            <div class="ml-4">
              <span class="font-semibold text-secondary">${area.years} ${area.years === 1 ? 'year' : 'years'}</span>
            </div>
          </div>
        `).join('')}
      </div>
    </div>
  `;

    this.popupService.info('Areas of Expertise', formattedContent);
  }

  /*
  private getAreasOfExpertise(areasOfExpertise: any): void {
    try {
      if (!areasOfExpertise) {
        throw new Error("No data provided");
      }

      // If it's already an object with the expected structure
      if (typeof areasOfExpertise === 'object' && !Array.isArray(areasOfExpertise) &&
        'series' in areasOfExpertise && 'labels' in areasOfExpertise) {
        this.areas_of_expertise = areasOfExpertise;
      }
      // If it's a string that needs to be parsed
      else if (typeof areasOfExpertise === 'string' && this.isValidJSON(areasOfExpertise)) {
        this.areas_of_expertise = JSON.parse(areasOfExpertise);
      }
      else {
        throw new Error("Invalid data format");
      }

      this.updateChartData();
    } catch (error) {
      console.error("[getAreasOfExpertise] Failed to process data", error);
      this.areas_of_expertise = { series: [], labels: [] };
    }
  }
  */

  private isValidJSON(jsonString: string): boolean {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (error) {
      return false;
    }
  }

  private updateChartData() {
    // console.log('updateChartData')

    if (this.areas_of_expertise?.series && this.areas_of_expertise.labels) {
      this.chartOptions.series = this.areas_of_expertise.series;
      this.chartOptions.labels = this.areas_of_expertise.labels;
      this.chartOptions.chart.type = "donut";
      this.chartOptions.colors = [];
      this.chartOptions.chart.height = "200px";

      if (window.innerWidth < 768) {
        this.chartOptions.legend.position = "bottom";
        this.chartOptions.legend.offsetY = 0;
      } else {
        this.chartOptions.legend.position = "right";
        this.chartOptions.legend.offsetY = 0;
      }

      this.cdr.detectChanges();
    }
  }

  private initChartOptions() {
    this.chartOptions = {
      series: [],
      colors: ["#F86C25"],
      chart: {
        type: "donut",
        sparkline: {
          enabled: true,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              name: {
                show: true,
                fontFamily: "Poppins, sans-serif",
                offsetY: 20,
              },
              total: {
                showAlways: true,
                show: true,
                fontFamily: "Poppins, sans-serif",
              },
              value: {
                show: true,
                fontFamily: "Poppins, sans-serif",
                offsetY: -20,
              },
            },
            size: "70%",
          },
        },
      },
      grid: {
        show: false,
        strokeDashArray: 4,
        padding: {
          left: 2,
          right: 2,
          bottom: 0,
        },
      },
      labels: [],
      legend: {
        show: true,
        position: "bottom",
        fontFamily: "Poppins, sans-serif",
        fontSize: '12px',
        labels: {
          colors: '#3c4277'
        },
        markers: {
          width: 0,
          height: 0,
          radius: 0,
          offsetX: 0
        },
        formatter: function(seriesName: string, opts: any) {
          // Truncate text if longer than 32 characters
          const truncatedName = seriesName.length > 32
            ? seriesName.substring(0, 32) + '...'
            : seriesName;
          return truncatedName;
        },
        itemMargin: {
          horizontal: 8,
          vertical: 4  // Adjust spacing between items
        },
      },
      tooltip: {
        enabled: true,
        x: {
          show: false,
        },
        y: {
          formatter: function(value: number) {
            return value + '%';
          }
        }
      }
    };
  }

  /*
  private initChartOptions() {
    //console.log('initChartOptions')

    this.chartOptions = {
      series: [],
      colors: ["#F86C25"],
      chart: {
        type: "donut",
        sparkline: {
          enabled: true,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              name: {
                show: true,
                fontFamily: "Poppins, sans-serif",
                offsetY: 20,
              },
              total: {
                showAlways: true,
                show: true,
                fontFamily: "Poppins, sans-serif",
              },
              value: {
                show: true,
                fontFamily: "Poppins, sans-serif",
                offsetY: -20,
              },
            },
            size: "70%", // This controls the donut hole size
          },
        },
      },
      grid: {
        show: false,
        strokeDashArray: 4,
        padding: {
          left: 2,
          right: 2,
          bottom: 0,
        },
      },
      labels: [],
      legend: {
        show: true,
        position: "bottom",
        fontFamily: "Poppins, sans-serif",
      },
      tooltip: {
        enabled: true,
        x: {
          show: false,
        },
      }
    };
  }
  */

}
